import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { SignDocCard } from '../../SignDoc/SignDocCard/SignDocCard';
import './LearningPathLayout.scss';
import { get } from 'lodash';
import LockIcon from '@mui/icons-material/Lock';
import WestIcon from '@mui/icons-material/West';
import { Confetti } from '../../Smart/Confetti/Confetti';
import completedStampSrc from "../../../assets/images/signDocs/complete-task-learning-path.png";
import { isSignDocComplete } from '../../SignDoc/SignDocLib';
import { isLearningPathUnordered } from '../../../Utils/SignDocs/SignDocs';


const HOURS_SINCE_COMPLETION_TO_SHOW_CONFETTI = 24;

/**
 * LearningPathLayout arranges learning path items
 * with flexible layout options (horizontal/vertical) and direction (LTR/RTL) support
 * Connected by arrows
 * Items are automatically marked as:
 * - completed (progressPercent=100 or signed=true)
 * - active (first non-completed item)
 * - locked (any item after the active one)
 */
const LearningPathLayout = ({
  items = [],
  rootSignDoc,
}) => {
  const [showConfetti, setShowConfetti] = useState(false);
  const [processedItems, setProcessedItems] = useState([]);
  const hasAutoScrolled = useRef(false);

  // Check if any item has just completed (100% progress)
  useEffect(() => {
    const rootProgress = get(rootSignDoc, 'progressPercent');
    if (rootProgress === 100) {
      const completedDate = new Date(get(rootSignDoc, 'completedOnDate'));
      const now = new Date();
      const hoursSinceCompletion = (now.getTime() - completedDate.getTime()) / (1000 * 60 * 60);
      
      if (hoursSinceCompletion <= HOURS_SINCE_COMPLETION_TO_SHOW_CONFETTI) {
        setShowConfetti(true);
      }
    }
  }, [items, rootSignDoc]);

  useEffect(() => {
    if (showConfetti) {
      const timer = setTimeout(() => {
        setShowConfetti(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [showConfetti]);

  // Free-path mode: when the root SignDoc has unorderedLearningPathTasks === true,
  // every non-completed item is `active` (nothing is `locked`).
  // `rootSignDoc` is passed from LearningPathViewer as the item wrapper (not a raw SignDoc),
  // so the SignDoc fields live at `rootSignDoc.signDoc.*`. Fall back to `rootSignDoc` itself
  // for callers that pass a raw SignDoc.
  const isUnordered = isLearningPathUnordered(get(rootSignDoc, 'signDoc', rootSignDoc));

  // Process items to determine their correct status
  useEffect(() => {
    if (!items || items.length === 0) return;

    // Make a copy to avoid mutating the original
    const itemsWithStatus = [...items];

    // Find the first non-completed item (active item)
    let activeItemIndex = -1;

    for (let i = 0; i < itemsWithStatus.length; i++) {
      const item = itemsWithStatus[i];

      if (isSignDocComplete({
        type: get(item, 'signDoc.type'),
        progressPercent: item.progressPercent,
        viewed: item.viewed,
        signed: item.signed
      })) {
        // Mark as completed using centralized completion logic
        itemsWithStatus[i] = { ...item, status: 'completed' };
      // Free-path: every non-completed item is active; never locked.
      } else if (isUnordered) {
        itemsWithStatus[i] = { ...item, status: 'active' };
      // Sequential mode: only the first non-completed item is active; the rest are locked.
      } else {
        // First non-completed item becomes active
        if (activeItemIndex === -1) {
          activeItemIndex = i;
          itemsWithStatus[i] = { ...item, status: 'active' };
        } else {
          // All subsequent items are locked
          itemsWithStatus[i] = { ...item, status: 'locked' };
        }
      }
    }

    setProcessedItems(itemsWithStatus);
  }, [items, isUnordered]);

  // Auto-scroll to the current active item once and highlight it briefly
  useEffect(() => {
    if (!processedItems || processedItems.length === 0 || hasAutoScrolled.current) return;

    const activeIndex = processedItems.findIndex((it) => get(it, 'status') === 'active');
    if (activeIndex === -1) return;

    const activeItem = processedItems[activeIndex];
    const domId = `learning-path-item-${get(activeItem, 'signDocId', activeIndex)}`;

    const scrollAndHighlight = () => {
      const el = document.getElementById(domId);
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
      el.classList.add('highlight-active-item');
      setTimeout(() => {
        el.classList.remove('highlight-active-item');
      }, 1300);
      hasAutoScrolled.current = true;
    };

    // Allow DOM to paint before trying to scroll and highlight
    setTimeout(scrollAndHighlight, 100);
  }, [processedItems]);

  // Get the appropriate status icon for the item status
  const getStatusIcon = (status) => {
    switch (status) {
      case 'locked':
        return <LockIcon style={{ color: '#A8A8A8', fontSize: '30px' }} />;
      case 'completed':
        return <img src={completedStampSrc} alt="Completed" className="status-icon completed-icon" />;
      default:
        return null;
    }
  };

  return (
    <div className={`learning-path-layout ${isUnordered ? 'learning-path-layout--unordered' : ''}`}>
      <div className="path-layout">
        {processedItems.map((item, index) => (
          <React.Fragment key={item.signDocId || index}>
            <div
              id={`learning-path-item-${item.signDocId || index}`}
              className={`path-item ${item.status}`}
            >
              <div className="card-container">
                <SignDocCard
                  imgSrc={get(item, 'signDoc.imgSrc')}
                  docName={get(item, 'signDoc.docName', 'Untitled')}
                  description={get(item, 'signDoc.description', '')}
                  progressPercent={get(item, 'progressPercent', 0)}
                  viewed={get(item, 'viewed', false)}
                  signed={get(item, 'signed', false)}
                  id={item.signDocId}
                  type={get(item, 'signDoc.type')}
                  doNotForceCompletion={true}
                  status={get(item, 'status')}
                  learnPathRootId={get(rootSignDoc, 'signDocId')}
                  statusIcon={getStatusIcon(get(item, 'status'))}
                  forceShowEndDate={get(item, 'status') === 'completed'}
                  linkUrl={get(item, 'signDoc.linkUrl')}
                  signDocAdditionalData={get(item, 'signDoc.additionalData', {})}
                  examScore={get(item, 'examScore', null)}
                />
              </div>
            </div>

            {/* Directional arrow between items — hidden in free-path mode (no order). */}
            {!isUnordered && index !== processedItems.length - 1 && (
              <div className="arrow-container">
                <WestIcon fontSize={'large'} className="path-arrow" />
              </div>
            )}
          </React.Fragment>
        ))}
      </div>
      <Confetti showConfetti={showConfetti} />
    </div>
  );
};

LearningPathLayout.propTypes = {
  /** Array of learning path items with signDoc and progress data */
  items: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    signDocId: PropTypes.string.isRequired,
    signDoc: PropTypes.object,
    progressPercent: PropTypes.number,
    viewed: PropTypes.bool,
    signed: PropTypes.bool,
    status: PropTypes.oneOf(['completed', 'active', 'locked']),
    icon: PropTypes.element,
    isRoot: PropTypes.bool,
  })),
  /** Root SignDoc of the learning path */
  rootSignDoc: PropTypes.object,
};

export default LearningPathLayout; 